import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from 'react'
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { bereich, markiereAmRand, zielAusZeiger, type Block } from './ziehen'

export interface Ziel {
  /** Block, an dem die Linie steht. */
  key: string
  /** `oben`: abgelegt wird davor; `unten`: ans Ende (hinter den letzten Block). */
  kante: 'oben' | 'unten'
}

function zeigerY(e: DragMoveEvent | DragStartEvent | DragEndEvent, dy = 0): number | null {
  const a = e.activatorEvent as MouseEvent | TouchEvent | null
  if (!a) return null
  const y = 'touches' in a ? a.touches[0]?.clientY : (a as MouseEvent).clientY
  return y === undefined ? null : y + dy
}

/**
 * Wo ein Druck **keine** Zeile greift (N-64 neu): in Feldern und Knöpfen wird
 * Text markiert und geklickt, am linken Rand wird markiert. Alles andere an
 * der Zeile ist Greiffläche.
 */
const NICHT_GREIFBAR = 'input, textarea, select, button, a, label, [contenteditable="true"], [data-rand], [data-nicht-greifen]'
const greifbar = (ziel: EventTarget | null) => !(ziel instanceof Element && ziel.closest(NICHT_GREIFBAR))

/** Maus: Start nach 4 px Weg (N-70), aber nur auf der freien Fläche. */
class ZeilenMaus extends MouseSensor {
  static activators: typeof MouseSensor.activators = [
    { eventName: 'onMouseDown', handler: (e, o) => greifbar(e.nativeEvent.target) && MouseSensor.activators[0].handler(e, o) },
  ]
}

/** Finger: Start nach 250 ms Halten, damit Wischen weiter scrollt (N-70). */
class ZeilenFinger extends TouchSensor {
  static activators: typeof TouchSensor.activators = [
    { eventName: 'onTouchStart', handler: (e, o) => greifbar(e.nativeEvent.target) && TouchSensor.activators[0].handler(e, o) },
  ]
}

/**
 * Zustand und Handler fürs Ziehen von Blöcken in `behaelter` (N-71). Die Blöcke
 * tragen `data-key`; gemessen wird bei jeder Bewegung neu, höchstens einmal je
 * Frame — die Höhen ändern sich, sobald eine Zeile auf- oder zuklappt.
 *
 * `onAblegen(key, vorKey)` kommt nur, wenn sich die Stelle ändert (`vorKey`
 * `null` = ans Ende). Welche Zeilen mitwandern (die markierten, N-69 neu),
 * entscheidet der Aufrufer. Esc bricht ab (dnd-kit), ein Tastatur-Sensor fehlt
 * bewusst: Knöpfe ↑ ↓ bleiben der Tastaturweg (N-63, N-70).
 */
export function useZeilenZiehen({
  behaelter,
  bloeckeFuer,
  onAblegen,
}: {
  behaelter: RefObject<HTMLElement>
  /**
   * Listen mit Ebenen (N-66): die Blöcke, unter denen die gezogene Zeile landen
   * darf — je Block sein Schlüssel und die Schlüssel seiner sichtbaren Zeilen
   * (eine Hauptposition samt Unterpositionen). Ohne sie ist jede `[data-key]`
   * im Behälter ein Block.
   */
  bloeckeFuer?: (key: string) => { key: string; zeilen: string[] }[]
  onAblegen: (key: string, vorKey: string | null) => void
}) {
  const [gezogen, setGezogen] = useState<string | null>(null)
  const [ziel, setZiel] = useState<Ziel | null>(null)
  const frame = useRef<number | null>(null)
  const letztes = useRef<Ziel | null>(null)
  const aktiv = useRef<string | null>(null)

  const sensors = useSensors(
    useSensor(ZeilenMaus, { activationConstraint: { distance: 4 } }),
    useSensor(ZeilenFinger, { activationConstraint: { delay: 250, tolerance: 5 } }),
  )

  const bloecke = useCallback((): Block[] => {
    const el = behaelter.current
    if (!el) return []
    if (bloeckeFuer && aktiv.current) {
      const zeile = (k: string) => el.querySelector<HTMLElement>(`[data-key="${CSS.escape(k)}"]`)
      return bloeckeFuer(aktiv.current).flatMap((b) => {
        const erste = zeile(b.zeilen[0] ?? b.key)
        const letzte = zeile(b.zeilen[b.zeilen.length - 1] ?? b.key)
        return erste && letzte ? [{ key: b.key, oben: erste.getBoundingClientRect().top, unten: letzte.getBoundingClientRect().bottom }] : []
      })
    }
    return [...el.querySelectorAll<HTMLElement>('[data-key]')].map((b) => {
      const r = b.getBoundingClientRect()
      return { key: b.dataset.key!, oben: r.top, unten: r.bottom }
    })
  }, [behaelter, bloeckeFuer])

  const bestimme = useCallback(
    (y: number | null) => {
      if (y === null) return
      const liste = bloecke()
      if (!liste.length) return
      const vor = zielAusZeiger(liste, y)
      const neu: Ziel = vor ? { key: vor, kante: 'oben' } : { key: liste[liste.length - 1].key, kante: 'unten' }
      letztes.current = neu
      setZiel((alt) => (alt?.key === neu.key && alt.kante === neu.kante ? alt : neu))
    },
    [bloecke],
  )

  const ende = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
    letztes.current = null
    aktiv.current = null
    document.documentElement.classList.remove('ziehen-aktiv')
    setGezogen(null)
    setZiel(null)
  }

  const dndProps = {
    sensors,
    onDragStart: (e: DragStartEvent) => {
      // Ein offenes Feld verliert den Fokus: Die Zeile speichert, bevor sie wandert.
      const offen = document.activeElement as HTMLElement | null
      offen?.blur?.()
      document.documentElement.classList.add('ziehen-aktiv')
      aktiv.current = String(e.active.id)
      setGezogen(String(e.active.id))
      bestimme(zeigerY(e))
    },
    onDragMove: (e: DragMoveEvent) => {
      if (frame.current !== null) return
      const y = zeigerY(e, e.delta.y)
      frame.current = requestAnimationFrame(() => {
        frame.current = null
        bestimme(y)
      })
    },
    onDragEnd: (e: DragEndEvent) => {
      const key = String(e.active.id)
      // Die letzte Bewegung kann noch im Frame warten — bei einer schnellen
      // Bewegung kam das Loslassen vorher, und die Zeile landete dort, wo der
      // Zeiger einen Frame zuvor stand. Das Ziel gilt an der Endposition.
      if (frame.current !== null) cancelAnimationFrame(frame.current)
      frame.current = null
      bestimme(zeigerY(e, e.delta.y))
      const z = letztes.current
      ende()
      if (!key || !z) return
      onAblegen(key, z.kante === 'oben' ? z.key : null)
    },
    onDragCancel: ende,
    autoScroll: true,
  }

  return { gezogen, ziel, dndProps }
}

/** Der Rahmen um eine ziehbare Liste — `DndContext` mit den Props aus `useZeilenZiehen`. */
export function ZiehBereich({ dndProps, vorschau, children }: { dndProps: ReturnType<typeof useZeilenZiehen>['dndProps']; vorschau: ReactNode; children: ReactNode }) {
  return (
    <DndContext {...dndProps}>
      {children}
      <DragOverlay dropAnimation={null}>{vorschau ? <ZiehVorschau>{vorschau}</ZiehVorschau> : null}</DragOverlay>
    </DndContext>
  )
}

/**
 * Macht eine ganze Zeile greifbar (N-64 neu): `ref` und `listeners` an das
 * Element der Zeile, dazu die Klasse `zeile-greifbar` (Zeiger, `index.css`).
 * Die `attributes` von dnd-kit bleiben bewusst weg — sie machten die Zeile
 * fokussierbar, und ein Fokus auf der Zeile klappt einen Zeilenblock auf.
 * Außerhalb eines `ZiehBereich` oder mit `aus` greift nichts.
 */
export function useZeileGreifen(id: string | undefined, aus = false) {
  const { listeners, setNodeRef, isDragging } = useDraggable({ id: id ?? 'ohne-schluessel', disabled: aus || !id })
  return { ref: setNodeRef, listeners: aus || !id ? undefined : listeners, isDragging }
}

/** Nur der Rahmen der Vorschau im `DragOverlay`; den Inhalt liefert der Aufrufer (N-67). */
export function ZiehVorschau({ children }: { children: ReactNode }) {
  return (
    <div className="z-50 max-w-sm truncate rounded-md border border-border px-3 py-1.5 text-sm shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
      {children}
    </div>
  )
}

/**
 * Der gespiegelte Pfeil am linken Rand — wie am Zeilenkopf einer
 * Tabellenkalkulation. Spitze oben rechts, dort liegt auch der Klickpunkt.
 */
const PFEIL =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22"><path d="M15 1 L15 17 L11 13 L8 20 L6 19 L9 12 L4 12 Z" fill="black" stroke="white" stroke-width="1.2" stroke-linejoin="round"/></svg>'
export const RAND_ZEIGER = `url("data:image/svg+xml,${encodeURIComponent(PFEIL)}") 15 1, pointer`

/**
 * Markieren am linken Rand (N-64 neu) und Aufheben durch einen Klick
 * anderswohin.
 *
 * - Klick: nur diese Zeile · Strg: dazu oder weg · Umschalt: Bereich vom Anker.
 * - Gedrückt am Rand entlangfahren markiert alle überfahrenen Zeilen.
 * - Strg+C kopiert die markierten Zeilen (`onKopieren`).
 * - Ein Klick irgendwo sonst hebt die Markierung auf. Ausgenommen sind der
 *   Rand, alles mit `data-auswahl-behalten` (die Knöpfe für die Markierten),
 *   Dialoge (`role="dialog"`) und
 *   ein Druck auf eine markierte Zeile, aus dem ein Ziehen wird — erst wenn die
 *   Maus dort ohne Weg losgelassen wird, war es ein Klick.
 */
export function useRandMarkieren({
  behaelter,
  reihe,
  markiert,
  setMarkiert,
  onKopieren,
}: {
  behaelter: RefObject<HTMLElement>
  /** Schlüssel der sichtbaren Zeilen in Reihenfolge. */
  reihe: readonly string[]
  markiert: ReadonlySet<string>
  setMarkiert: (m: Set<string>) => void
  /**
   * Strg+C (Mac: Cmd+C) kopiert die markierten Zeilen — solange kein Feld den
   * Fokus hat und kein Text auf der Seite markiert ist; dann bleibt es
   * gewöhnliches Textkopieren (Fokusregel 5.8).
   */
  onKopieren?: () => void
}) {
  const anker = useRef<string | null>(null)

  const keyUnter = (y: number): string | null => {
    const zeilen = [...(behaelter.current?.querySelectorAll<HTMLElement>('[data-key]') ?? [])]
    if (!zeilen.length) return null
    for (const z of zeilen) if (y < z.getBoundingClientRect().bottom) return z.dataset.key!
    return zeilen[zeilen.length - 1].dataset.key!
  }

  const amRand = (key: string) => (e: ReactMouseEvent) => {
    if (e.button !== 0) return
    // Keine Textauswahl. Ein Feld mit Fokus gibt ihn ab (die Zeile speichert),
    // und eine fokussierbare Zeile bekommt ihn — dann wirken Strg+C, Entf und
    // Alt+Pfeile auf die markierten Zeilen statt auf Text.
    e.preventDefault()
    ;(document.activeElement as HTMLElement | null)?.blur?.()
    const zeile = (e.currentTarget as HTMLElement).closest<HTMLElement>('[data-key]')
    if (zeile?.hasAttribute('tabindex')) zeile.focus({ preventScroll: true })
    const art = e.shiftKey && anker.current ? 'bereich' : e.ctrlKey || e.metaKey ? 'umschalten' : 'neu'
    const start = art === 'bereich' ? anker.current! : key
    if (art !== 'bereich') anker.current = key
    const vorher = art === 'umschalten' ? new Set(markiert) : new Set<string>()
    setMarkiert(markiereAmRand(reihe, markiert, anker.current, key, art))

    let bewegt = false
    const alt = document.body.style.cursor
    document.body.style.cursor = RAND_ZEIGER
    const zug = (ev: MouseEvent) => {
      const k = keyUnter(ev.clientY)
      if (!k || (!bewegt && k === key)) return
      bewegt = true
      const n = new Set(vorher)
      for (const x of bereich(reihe, start, k)) n.add(x)
      setMarkiert(n)
    }
    const los = () => {
      document.body.style.cursor = alt
      document.removeEventListener('mousemove', zug)
      document.removeEventListener('mouseup', los)
    }
    document.addEventListener('mousemove', zug)
    document.addEventListener('mouseup', los)
  }

  useEffect(() => {
    if (!markiert.size) return
    let druck: { x: number; y: number } | null = null
    const unten = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null
      // Dialoge arbeiten mit der Markierung (Einfügen nach der markierten Zeile).
      if (!el || el.closest('[data-rand], [data-auswahl-behalten], [role="dialog"]')) return
      const zeile = el.closest<HTMLElement>('[data-key]')
      if (zeile && markiert.has(zeile.dataset.key!) && behaelter.current?.contains(zeile) && greifbar(el)) {
        druck = { x: e.clientX, y: e.clientY }
        return
      }
      setMarkiert(new Set())
    }
    const oben = (e: MouseEvent) => {
      if (druck && Math.hypot(e.clientX - druck.x, e.clientY - druck.y) < 4) setMarkiert(new Set())
      druck = null
    }
    document.addEventListener('mousedown', unten, true)
    document.addEventListener('mouseup', oben, true)
    return () => {
      document.removeEventListener('mousedown', unten, true)
      document.removeEventListener('mouseup', oben, true)
    }
  }, [markiert, behaelter, setMarkiert])

  const kopieren = useRef(onKopieren)
  kopieren.current = onKopieren
  useEffect(() => {
    if (!markiert.size) return
    const taste = (e: KeyboardEvent) => {
      // Eine Liste mit eigenem Tastenweg (Positionsraster) hat die Taste schon behandelt.
      if (e.defaultPrevented || !kopieren.current) return
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'c') return
      const fokus = document.activeElement
      if (fokus instanceof Element && fokus.closest('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]')) return
      if (window.getSelection()?.toString()) return
      e.preventDefault()
      kopieren.current()
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
  }, [markiert])

  return { amRand }
}
